import { useMemo, useState } from 'react';
import { useTasksStore } from '@/stores/tasksStore';
import { TaskUI } from '@flexiplanner/types';
import { TaskBlock } from '@/components/blocks/TaskBlock';
import { TaskEditor } from '@/components/blocks/TaskEditor';
import { api } from '@/lib/api';
import { taskToUI, numberToPriority } from '@flexiplanner/types';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  useDroppable,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

type KanbanColumn = 'todo' | 'in_progress' | 'done';

const columns: { id: KanbanColumn; title: string; color: string }[] = [
  { id: 'todo', title: 'To Do', color: 'text-blue-500' },
  { id: 'in_progress', title: 'In Progress', color: 'text-yellow-500' },
  { id: 'done', title: 'Done', color: 'text-green-500' },
];

function KanbanColumnPanel({
  column,
  count,
  children,
}: {
  column: (typeof columns)[number];
  count: number;
  children: React.ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: column.id });

  return (
    <div ref={setNodeRef} className="w-[300px] flex-shrink-0">
      <Card className={cn('flex h-full flex-col rounded-md border bg-muted/20 shadow-none transition-colors', isOver && 'border-primary/70 bg-primary/5')}>
        <CardHeader className="px-3 pb-2 pt-3">
          <div className="flex items-center justify-between">
            <CardTitle className={cn('text-sm font-semibold', column.color)}>
              {column.title}
            </CardTitle>
            <Badge variant="secondary" className="rounded-sm px-1.5 text-[10px]">{count}</Badge>
          </div>
        </CardHeader>
        <CardContent className="flex-1 overflow-y-auto px-2 pb-3 pt-0">{children}</CardContent>
      </Card>
    </div>
  );
}

export function KanbanView() {
  const { tasks, updateTask, deleteTask } = useTasksStore();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor)
  );

  const [activeTask, setActiveTask] = useState<TaskUI | null>(null);
  const [editingTask, setEditingTask] = useState<TaskUI | null>(null);

  const tasksByColumn = useMemo(() => {
    const grouped: Record<KanbanColumn, TaskUI[]> = {
      todo: [],
      in_progress: [],
      done: [],
    };

    tasks.forEach((task) => {
      const status = (task.status as string);
      if (status === 'completed' && grouped['done']) {
        grouped['done'].push(task);
      } else if (grouped[status as KanbanColumn]) {
        grouped[status as KanbanColumn].push(task);
      }
    });

    return grouped;
  }, [tasks]);

  const handleDragStart = (event: DragStartEvent) => {
    const task = tasks.find((t) => t.id === event.active.id);
    setActiveTask(task || null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveTask(null);

    if (!over) return;

    const taskId = active.id as string;
    const overId = over.id as string;
    const newStatus = columns.some(column => column.id === overId)
      ? overId
      : tasks.find(task => task.id === overId)?.status;

    if (!newStatus || !columns.some(column => column.id === newStatus)) return;

    // Update activity status locally
    updateTask(taskId, { status: newStatus as any });
    
    // Update remote
    api.tasks.update(taskId, { status: newStatus }).catch((err) => {
      console.error('Failed to update activity status', err);
    });
  };

  const handleStatusChange = (task: TaskUI, status: KanbanColumn) => {
    if (task.status === status || (task.status === 'completed' && status === 'done')) return;
    updateTask(task.id, { status });
    api.tasks.update(task.id, { status }).catch((err) => {
      console.error('Failed to update activity status', err);
      updateTask(task.id, { status: task.status });
    });
  };

  const handleSaveTask = async (updates: Partial<TaskUI>) => {
    if (!editingTask) return;
    
    try {
      const input = {
        title: updates.title,
        description: updates.description,
        bundle_id: updates.bundleId,
        due_date: updates.deadline,
        do_date: updates.scheduledStart,
        do_date_end: updates.scheduledEnd,
        duration_minutes: updates.estimatedDuration,
        priority: updates.priority ? numberToPriority(updates.priority) : undefined,
        state_profile: updates.stateProfile,
        custom_attributes: updates.customAttributes,
      };
      const response = await api.tasks.update(editingTask.id, input);
      if (response.success && response.data) {
        updateTask(editingTask.id, taskToUI((response.data as any).task || response.data));
      } else {
        updateTask(editingTask.id, updates); // Optimistic fallback
      }
    } catch (err) {
      console.error('Failed to save activity', err);
      updateTask(editingTask.id, updates); // Optimistic fallback
    }
  };

  const handleDeleteTask = async () => {
    if (!editingTask) return;
    
    try {
      await api.tasks.delete(editingTask.id);
      deleteTask(editingTask.id);
      setEditingTask(null);
    } catch (err) {
      console.error('Failed to delete activity', err);
    }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="border-b pb-4">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>↗</span>
          <span>Activities</span>
        </div>
        <h2 className="mt-2 text-2xl font-semibold tracking-normal">Activities board</h2>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Badge variant="secondary" className="rounded-md">Board</Badge>
          <Badge variant="outline" className="rounded-md">Bundle required</Badge>
          <Badge variant="outline" className="rounded-md">Projects from bundles</Badge>
        </div>
      </div>

      <div className="flex-1 overflow-x-auto py-4">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div className="flex h-full min-w-max gap-3">
            {columns.map((column) => {
              const columnTasks = tasksByColumn[column.id];

              return (
                <KanbanColumnPanel key={column.id} column={column} count={columnTasks.length}>
                      <SortableContext
                        items={columnTasks.map((t) => t.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        <div className="space-y-2">
                          {columnTasks.map((task) => (
                            <div key={task.id} className="rounded-lg border bg-background/40 p-2">
                              <TaskBlock
                                task={task}
                                onEdit={() => setEditingTask(task)}
                              />
                              <div className="mt-2 grid grid-cols-2 gap-1 sm:grid-cols-4">
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="h-8 text-xs"
                                  onClick={() => setEditingTask(task)}
                                >
                                  Edit
                                </Button>
                                {columns.map(statusColumn => (
                                  <Button
                                    key={statusColumn.id}
                                    type="button"
                                    variant={task.status === statusColumn.id || (task.status === 'completed' && statusColumn.id === 'done') ? 'default' : 'outline'}
                                    size="sm"
                                    className="h-8 text-xs"
                                    onClick={() => handleStatusChange(task, statusColumn.id)}
                                  >
                                    {statusColumn.id === 'in_progress' ? 'Doing' : statusColumn.title}
                                  </Button>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </SortableContext>
                </KanbanColumnPanel>
              );
            })}
          </div>

          <DragOverlay>
            {activeTask ? (
              <div className="opacity-80">
                <TaskBlock
                  task={activeTask}
                  onEdit={() => {}}
                />
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>
      <TaskEditor
        task={editingTask}
        open={!!editingTask}
        onClose={() => setEditingTask(null)}
        onSave={handleSaveTask}
        onDelete={handleDeleteTask}
      />
    </div>
  );
}
